// @team Subscriptions - Programs & Upsells
import { logError } from '@bfa/nextjs-common/utils';
import GET_GWS_ELIGIBILITY_CHECK from 'graphql/queries/GET_GWS_ELIGIBILITY_CHECK';
import flow from 'lodash/flow';
import { NextPage, NextPageContext } from 'next';
import React from 'react';

import { ABSOLUTE_URLS } from '../../constants';
import { ELIGIBLITY_ERROR_MESSAGE } from '../../features/Popups/pages/utils/annualUpgrade';
import { GET_MEMBERSHIP_COSTS } from '../../features/Reactivate/graphql/queries/GET_MEMBERSHIP_COSTS';
import GET_ONE_CLICK_LANDING_PAGE from '../../features/Upgrades/graphql/queries/GET_ONE_CLICK_LANDING_PAGE';
import { getShippingCountry } from '../../features/Upgrades/hooks/useUpgradeGbxCheckout';
import GbxOneClickUpgrade, {
  GbxOneClickUpgradeProps,
} from '../../features/Upgrades/pages/gbxOneClick';
import {
  GQLCountry,
  GQLGwsType,
  GQLLayoutContent,
  GQLProgram,
} from '../../gql.generated';
import { initApolloClient, withApolloContext } from '../../utils/apollo';
import { getFacebookEventsProps } from '../../utils/facebookEvents/utils/index';
import { getGqlProgram } from '../../utils/getGqlProgram';
import { getGtmEventsProps } from '../../utils/gtmEvents/utils/getGtmEventsProps';
import { getGwsResponse } from '../../utils/gws';
import redirectTo from '../../utils/redirectTo';
import { CmsTextAsset } from '../../utils/types';

const GbxOneClickPage: NextPage<GbxOneClickUpgradeProps> = (props) => (
  <GbxOneClickUpgrade {...props} />
);

const isEnabled = (key?: string) => {
  if (!key) return false;

  return ['TRUE', 'ON', '1'].includes(key.toUpperCase());
};

GbxOneClickPage.getInitialProps = async (ctx: NextPageContext) => {
  try {
    const client = initApolloClient({}, ctx);
    const {
      fromCheckout,
      fromAddons,
      fromUP,
      program,
      noCheckout,
      subscriptionSuccessfullyCharged,
    } = ctx.query;
    const isComingFromCheckout = fromCheckout === 'true';
    const isComingFromAddons = fromAddons === 'true';
    const isComingFromUP = fromUP === 'true';
    const { data, error } = await client.query({
      query: GET_ONE_CLICK_LANDING_PAGE,
    });

    const gwsProgram = GQLProgram.GBX;
    const gwsType = GQLGwsType.ICONBOX_UPGRADE_GIFT;

    const isRebootRenameOn = isEnabled(data?.me?.layout?.rebootRename);
    const shippingCountry = getShippingCountry(data?.me?.subscriptions);
    const { data: membershipCostsData } = await client.query({
      query: GET_MEMBERSHIP_COSTS,
      variables: {
        program: GQLProgram.GBX,
        country: shippingCountry as GQLCountry,
        period: 1,
      },
    });

    const {
      data: gwsData,
      loading: gwsLoading,
      error: gwsError,
    } = await client.query({
      query: GET_GWS_ELIGIBILITY_CHECK,
      variables: {
        gwsType,
        program: gwsProgram,
      },
    });

    const eligibilityResponse = getGwsResponse(
      gwsType,
      gwsData?.me?.checkUserEligibilityForGws,
      { program: gwsProgram },
      gwsLoading,
      gwsError
    );

    if (gwsError) {
      logError(Error(ELIGIBLITY_ERROR_MESSAGE));
      redirectTo(ABSOLUTE_URLS.GLAM_BAG, ctx);
    }

    if (error) {
      logError(
        Error(
          `Error while trying to get gbx one click landing page. ${error.message}`
        )
      );
      redirectTo(ABSOLUTE_URLS.GLAM_BAG, ctx);
    }

    const layout = data?.me?.layout as GQLLayoutContent & {
      paymentRetrialEnabled: string;
      gbxMetaTitle?: CmsTextAsset;
      gbxMetaDescription?: CmsTextAsset;
      consent?: CmsTextAsset;
    };

    if (!layout) {
      throw new Error('No layout found');
    }

    const {
      gbxOneClickUpgradePage,
      paymentRetrialEnabled,
      gbxMetaTitle,
      gbxMetaDescription,
      consent,
    } = layout;

    if (!gbxOneClickUpgradePage) {
      redirectTo(
        isComingFromCheckout
          ? ABSOLUTE_URLS.MEMBERSHIP_CONFIRMATION
          : ABSOLUTE_URLS.GLAM_BAG,
        ctx
      );
    }

    return {
      error: false,
      fireSubscriptionPaymentSuccessPixel:
        subscriptionSuccessfullyCharged === 'true',
      facebookEventsProps: getFacebookEventsProps(ctx),
      gbxOneClickUpgradePage,
      program: getGqlProgram(program as string) || GQLProgram.GLAMBAG,
      paymentRetryEnabled: paymentRetrialEnabled?.toUpperCase() === 'ON',
      fromCheckout: isComingFromCheckout,
      fromAddons: isComingFromAddons,
      fromUP: isComingFromUP,
      noCheckout: noCheckout === 'true',
      gtmEventsProps: isComingFromCheckout
        ? await getGtmEventsProps(ctx, 'Subscriptions - Programs & Upsells')
        : undefined,
      gbxMetaTitle: gbxMetaTitle?.value,
      gbxMetaDescription: gbxMetaDescription?.value,
      isNotEligible: !!eligibilityResponse?.error,
      consent: consent?.value,
      shippingCost:
        membershipCostsData?.me?.membershipCosts?.shippingCost?.amount,
      isRebootRenameOn: isRebootRenameOn,
    };
  } catch (e) {
    logError(e);
    return { error: true, fireSubscriptionPaymentSuccessPixel: false };
  }
};

const Page = flow(withApolloContext({ ssr: true }))(GbxOneClickPage);

Page.displayName = 'GbxOneClickUpgrade';

export default Page;
